import { FiEye, FiCheck } from "@/lib/icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  useColorblindMode,
  COLORBLIND_LABELS,
  type ColorblindMode,
} from "@/hooks/useColorblindMode";

interface ModeConfig {
  mode: ColorblindMode;
  icon: React.ReactNode;
  colors: string[];
}

const modeConfigs: ModeConfig[] = [
  {
    mode: "normal",
    icon: <FiEye className="size-4" />,
    colors: ["bg-red-500", "bg-green-500", "bg-blue-500"],
  },
  {
    mode: "protanopia",
    icon: (
      <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v8M8 12h8" strokeLinecap="round" />
      </svg>
    ),
    colors: ["bg-yellow-600", "bg-yellow-400", "bg-blue-500"],
  },
  {
    mode: "deuteranopia",
    icon: (
      <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <path d="M8 8l8 8M16 8l-8 8" strokeLinecap="round" />
      </svg>
    ),
    colors: ["bg-amber-500", "bg-amber-300", "bg-blue-500"],
  },
  {
    mode: "tritanopia",
    icon: (
      <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v12" strokeLinecap="round" />
      </svg>
    ),
    colors: ["bg-red-500", "bg-cyan-500", "bg-pink-400"],
  },
  {
    mode: "achromatopsia",
    icon: (
      <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="4" fill="currentColor" />
      </svg>
    ),
    colors: ["bg-gray-700", "bg-gray-500", "bg-gray-300"],
  },
];

export function ColorblindToggle() {
  const { mode, setMode } = useColorblindMode();
  const isActive = mode !== "normal";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={`relative ${isActive ? "text-primary" : ""}`}
          aria-label="Accessibility: Color vision settings"
          title="Color vision accessibility"
        >
          <FiEye className="size-4" />
          {isActive && (
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-accent rounded-full" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="flex items-center gap-2 text-sm font-medium">
          <FiEye className="size-4" />
          Color Vision
        </DropdownMenuLabel>
        <p className="px-2 pb-2 text-xs text-muted-foreground">
          Adjust colors for better visibility
        </p>
        <DropdownMenuSeparator />
        <div className="p-1">
          {modeConfigs.map((config) => (
            <DropdownMenuItem
              key={config.mode}
              onClick={() => setMode(config.mode)}
              className={`cursor-pointer rounded-lg mb-1 last:mb-0 ${
                mode === config.mode
                  ? "bg-accent/50 border border-accent"
                  : "hover:bg-muted"
              }`}
            >
              <div className="flex items-center justify-between w-full py-1">
                <div className="flex items-center gap-3">
                  <span className="text-muted-foreground">{config.icon}</span>
                  <div>
                    <p className="text-sm font-medium">
                      {COLORBLIND_LABELS[config.mode]}
                    </p>
                    <div className="flex gap-1 mt-1">
                      {config.colors.map((color, i) => (
                        <span
                          key={i}
                          className={`w-3 h-3 rounded-full ${color}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                {mode === config.mode && (
                  <FiCheck className="size-4 text-primary" />
                )}
              </div>
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
